import Button from "@bmi-digital/components/button";
import ArrowForwardIcon from "@bmi-digital/components/icon/ArrowForward";
import ShoppingCart from "@bmi-digital/components/icon/ShoppingCart";
import Section from "@bmi-digital/components/section";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import React, { useEffect, useMemo, useState } from "react";
import {
  ACTION_TYPES,
  useBasketContext
} from "../contexts/SampleBasketContext";
import { createActionLabel } from "../utils/createActionLabelForAnalytics";
import { getPathWithCountryCode } from "../utils/path";
import FormSection, { FormData } from "./FormSection";
import RichText from "./RichText";
import { SampleBasketSectionData } from "./SampleBasketBase";
import SampleBasketSectionProducts from "./SampleBasketSectionProducts";
import { useSiteContext } from "./Site";
import { getCTA } from "./link/utils";
import { StyledCompleteButtonContainer } from "./styles/SampleBasketSection.styles";

type SampleOrderElementWithOptionalFields = Record<
  string,
  string | undefined | null
>;

const formatSamples = (
  samples: SampleOrderElementWithOptionalFields[],
  formSource: FormData["source"]
) => {
  const lineBreakCharacter = formSource === "CMS" ? "<br/>" : "\n";

  return samples
    .map((sample) =>
      Object.entries(sample)
        .filter(([_, value]) => !!value)
        .map(([key, value]) => `${key}: ${value}`)
        .join(`${lineBreakCharacter}`)
    )
    .join(`${lineBreakCharacter}${lineBreakCharacter}`);
};

const SampleBasketSection = ({
  data: {
    richTextDescription: description,
    checkoutFormSection,
    emptyBasketMessage,
    browseProductsCTALabel,
    browseProductsCTA
  }
}: {
  data: SampleBasketSectionData;
}) => {
  const [isCompleteFormShow, setCompleteFormShow] = useState(false);
  const [hasSamplesInTheBasket, setHasSamplesInTheBasket] = useState(false);
  const { basketState, basketDispatch } = useBasketContext();

  const { getMicroCopy, countryCode } = useSiteContext();

  useEffect(() => {
    setHasSamplesInTheBasket(basketState && basketState.products.length > 0);
  }, [basketState]);

  const handleCompleteClick = () => setCompleteFormShow(true);
  const handleSuccess = () =>
    basketDispatch({ type: ACTION_TYPES.BASKET_CLEAR });

  const actionLabels = useMemo(
    () =>
      basketState.products.map((sample) =>
        createActionLabel(
          sample.name,
          sample.colour,
          sample.textureFamily,
          sample.measurements
        )
      ),
    [basketState]
  );

  const sampleBasketCompleteOrderButtonLabel = getMicroCopy(
    microCopy.PDP_OVERVIEW_COMPLETE_SAMPLE_ORDER
  );

  const samples = useMemo(() => {
    const samples =
      basketState.products.map<SampleOrderElementWithOptionalFields>(
        (sample) => ({
          [getMicroCopy(microCopy.SAMPLE_BASKET_FORM_PRODUCT_CODE_FIELD)]:
            sample.code,
          [getMicroCopy(microCopy.SAMPLE_BASKET_FORM_PRODUCT_NAME_FIELD)]:
            sample.name,
          [getMicroCopy(microCopy.SAMPLE_BASKET_FORM_PRODUCT_COLOR_FIELD)]:
            sample.colour,
          [getMicroCopy(
            microCopy.SAMPLE_BASKET_FORM_PRODUCT_TEXTURE_FAMILY_FIELD
          )]: sample.textureFamily,
          [getMicroCopy(
            microCopy.SAMPLE_BASKET_FORM_PRODUCT_MEASUREMENTS_FIELD
          )]: sample.measurements,
          [getMicroCopy(microCopy.SAMPLE_BASKET_FORM_PRODUCT_URL_FIELD)]: `${
            window.location.origin
          }${getPathWithCountryCode(countryCode, sample.path)}`
        })
      );

    return formatSamples(samples, checkoutFormSection[0].source);
  }, [basketState, countryCode, getMicroCopy, checkoutFormSection]);

  const basketCta = getCTA(
    browseProductsCTA.value,
    countryCode,
    browseProductsCTALabel
  );
  return (
    <>
      <Section backgroundColor="white" data-testid="sample-basket-section">
        <RichText document={description} />
        <SampleBasketSectionProducts />
        {!hasSamplesInTheBasket && <RichText document={emptyBasketMessage} />}

        {!hasSamplesInTheBasket && basketCta && (
          <Button
            {...basketCta}
            endIcon={<ArrowForwardIcon />}
            variant="outlined"
            data-testid="sample-basket-section-browse-product"
          >
            {browseProductsCTALabel}
          </Button>
        )}
        {!isCompleteFormShow && hasSamplesInTheBasket && (
          <StyledCompleteButtonContainer>
            <Button
              endIcon={<ShoppingCart />}
              onClick={handleCompleteClick}
              data-testid="sample-basket-section-complete-order"
              gtm={{
                id: "cta-click1",
                label: sampleBasketCompleteOrderButtonLabel
              }}
            >
              {sampleBasketCompleteOrderButtonLabel}
            </Button>
          </StyledCompleteButtonContainer>
        )}
      </Section>
      {isCompleteFormShow && hasSamplesInTheBasket && (
        <Section data-testid="sample-basket-section-with-samples">
          <FormSection
            id="sample-basket-form"
            data={checkoutFormSection[0]}
            backgroundColor="pearl"
            additionalValues={{
              samples
            }}
            isSubmitDisabled={samples.length === 0}
            gtmOverride={{
              label: "samples ordering basket form submitted",
              action: actionLabels.join(", ")
            }}
            onSuccess={handleSuccess}
            data-testid={"sample-basket-section-checkout-form"}
          />
        </Section>
      )}
    </>
  );
};

export default SampleBasketSection;
